// js/views/templates.js
import { staticFetch, TEMPLATES_BASE_URL } from "../api.js";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const joinBaseUrl = (baseUrl, assetPath) => {
    const rawPath = String(assetPath || "").trim();
    if (!rawPath) return "";
    if (isAbsoluteUrl(rawPath)) return rawPath;

    const cleanBase = String(baseUrl || "").replace(/\/+$/, "");
    const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return `${cleanBase}${cleanPath}`;
};

const appendCacheBust = (url, cacheBust) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${encodeURIComponent(cacheBust)}`;
};

const normalizeTemplate = (tpl, cacheBust) => {
    const category = tpl?.categoria || tpl?.category || tpl?.manifest?.category || "Geral";
    const vertical =
        tpl?.vertical ||
        tpl?.name ||
        tpl?.title ||
        tpl?.manifest?.name ||
        tpl?.manifest?.title ||
        "Template";
    const description =
        tpl?.home?.title ||
        tpl?.manifest?.description ||
        "Pronto para ser editado e publicado.";
    const thumbnailSource =
        tpl?.catalogImage ||
        tpl?.thumbnail_url ||
        tpl?.thumbnail ||
        tpl?.preview?.thumbnail ||
        "";
    const previewSource =
        tpl?.preview_url ||
        tpl?.preview?.url ||
        tpl?.url ||
        (tpl?.slug ? `/${tpl.slug}/` : "");

    return {
        category,
        vertical,
        description,
        safeTitle: description || vertical,
        templateId: `${category}/${vertical}`,
        thumbUrl: appendCacheBust(
            thumbnailSource
                ? joinBaseUrl(TEMPLATES_BASE_URL, thumbnailSource)
                : `https://via.placeholder.com/400x250?text=${encodeURIComponent(vertical)}`,
            cacheBust
        ),
        demoUrl: joinBaseUrl(TEMPLATES_BASE_URL, previewSource),
    };
};

export const TemplatesView = {
    availableTemplates: [],

    render: async () => {
        try {
            const token = localStorage.getItem("heyweb_token");
            const cacheBust = Date.now();
            const response = await staticFetch(`/templates.json?v=${cacheBust}`, { method: "GET" });
            if (!response.ok) {
                throw new Error(`Falha ao carregar catalogo (HTTP ${response.status})`);
            }

            TemplatesView.availableTemplates = await response.json();

            if (!Array.isArray(TemplatesView.availableTemplates) || TemplatesView.availableTemplates.length === 0) {
                return `
                    <div style="text-align: center; padding: 4rem; color: #64748b;">
                        <div style="font-size: 3rem; margin-bottom: 1rem; opacity: 0.3;">📂</div>
                        <p style="font-size: 1.125rem; font-weight: 600;">Catalogo vazio.</p>
                        <p style="font-size: 0.875rem;">
                          Verifica se o Eleventy esta a gerar o array de dados corretamente.
                        </p>
                    </div>
                `;
            }

            const normalizedTemplates = TemplatesView.availableTemplates.map((tpl) => normalizeTemplate(tpl, cacheBust));
            const categories = [...new Set(normalizedTemplates.map((tpl) => tpl.category))].sort((a, b) => a.localeCompare(b));

            const filterButtons = [
                `<button type="button" class="template-filter active" data-category="all"
                  style="padding:0.75rem 1rem; border-radius:999px; border:1px solid #dbe4f0; background:#0f172a; color:white; font-weight:800; cursor:pointer; transition:all 0.2s ease;">
                  Todas
                </button>`,
                ...categories.map((category) => `
                <button type="button" class="template-filter" data-category="${category}"
                  style="padding:0.75rem 1rem; border-radius:999px; border:1px solid #dbe4f0; background:white; color:#334155; font-weight:800; cursor:pointer; transition:all 0.2s ease;">
                  ${category}
                </button>
                `),
            ].join("");

            const cards = normalizedTemplates
                .map((template) => {
                    const {
                        category,
                        vertical,
                        description,
                        safeTitle,
                        templateId,
                        thumbUrl,
                        demoUrl,
                    } = template;

                    const editorHash = token ? `#editor/new/${category}/${vertical}` : "#login";
                    const chooseLabel = token ? "Escolher Este" : "Entrar para Usar";

                    return `
            <div class="template-card animate-fade-in"
              style="background:white; border:1px solid #e2e8f0; border-radius:1rem; overflow:hidden; transition:all 0.3s ease; box-shadow:0 4px 6px -1px rgba(0,0,0,0.05); display:flex; flex-direction:column;"
              data-template-id="${templateId}"
              data-category="${category}">
              
              <div style="position:relative; height:200px; background:#f8fafc; display:flex; align-items:center; justify-content:center; overflow:hidden; border-bottom:1px solid #f1f5f9;">
                <img src="${thumbUrl}" alt="${safeTitle}" style="width:100%; height:100%; object-fit:cover;">

                <div class="overlay"
                  style="position:absolute; inset:0; background:rgba(15, 23, 42, 0.9); display:flex; flex-direction:column; gap:0.65rem; align-items:center; justify-content:center; opacity:0; transition:opacity 0.3s ease; backdrop-filter:blur(4px);"
                  onmouseover="this.style.opacity='1'"
                  onmouseout="this.style.opacity='0'">

                  <button
                    onclick="window.open('${demoUrl}', '_blank', 'noopener,noreferrer')"
                    style="background:white; color:#0f172a; border:none; padding:0.8rem 1.5rem; border-radius:0.6rem; font-weight:900; cursor:pointer; width:180px; transition:transform 0.2s;"
                    onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                    Ver Demo
                  </button>

                  <button
                    onclick="window.location.hash='${editorHash}'"
                    style="background:#3b82f6; color:white; border:none; padding:0.8rem 1.5rem; border-radius:0.6rem; font-weight:900; cursor:pointer; width:180px; transition:transform 0.2s;"
                    onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                    ${chooseLabel}
                  </button>
                </div>
              </div>

              <div style="padding:1.5rem; flex-grow:1; display:flex; flex-direction:column;">
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.75rem;">
                  <span style="font-size:0.65rem; font-weight:900; text-transform:uppercase; color:#3b82f6; background:#eff6ff; padding:0.25rem 0.6rem; border-radius:0.4rem; letter-spacing:0.05em;">
                    ${category}
                  </span>
                  <span style="font-size:0.7rem; color:#94a3b8; font-weight:600;">11ty Native</span>
                </div>
                
                <h3 style="margin:0; font-size:1.15rem; color:#1e293b; font-weight:900; line-height:1.3; flex-grow:1;">
                  ${vertical}
                </h3>
                
                <p style="margin-top:0.75rem; font-size:0.8rem; color:#64748b; line-height:1.4;">
                   ${description}
                </p>

                <div style="margin-top:1rem; padding-top:1rem; border-top:1px solid #f1f5f9; display:flex; align-items:center; gap:0.5rem;">
                   <div style="width:8px; height:8px; background:#10b981; border-radius:50%;"></div>
                   <span style="font-size:0.7rem; color:#64748b; font-weight:600;">Renderizado em 0.1ms</span>
                </div>
              </div>
            </div>
          `;
                })
                .join("");

            return `
        <div class="animate-fade-in" style="max-width:1400px; margin:0 auto; padding:1rem 0 0;">
          <div style="display:flex; flex-wrap:wrap; gap:0.75rem; margin-bottom:2rem;">
            ${filterButtons}
          </div>

          <div class="templates-grid" id="templates-grid"
            style="display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:2.5rem;">
            ${cards}
          </div>
        </div>
      `;
        } catch (error) {
            console.error("Erro ao carregar templates:", error);
            return `
        <div style="margin:4rem auto; max-width:540px; background:#fff1f2; border:1px solid #fda4af; border-radius:1.5rem; padding:3rem; text-align:center;">
          <div style="font-size:3.5rem; margin-bottom:1.5rem;">🚀</div>
          <h2 style="font-size:1.5rem; font-weight:900; color:#9f1239; margin-bottom:0.75rem;">Motor de Templates Offline</h2>
          <p style="color:#be123c; line-height:1.6; margin-bottom:1.5rem;">
            O CMS nao conseguiu comunicar com o gerador Eleventy. Certifica-te que o projeto stw-site esta a correr na porta 8080.
          </p>
          <button onclick="location.reload()"
            style="background:#e11d48; color:white; border:none; padding:1rem 2.5rem; border-radius:0.75rem; cursor:pointer; font-weight:900; box-shadow:0 4px 14px rgba(225, 29, 72, 0.4);">
            Tentar Reconectar
          </button>
        </div>
      `;
        }
    },

    afterRender: async () => {
        const filterButtons = [...document.querySelectorAll(".template-filter")];
        const cards = [...document.querySelectorAll(".template-card")];

        filterButtons.forEach((button) => {
            button.addEventListener("click", () => {
                const selectedCategory = button.dataset.category || "all";

                filterButtons.forEach((current) => {
                    const isActive = current === button;
                    current.classList.toggle("active", isActive);
                    current.style.background = isActive ? "#0f172a" : "white";
                    current.style.color = isActive ? "white" : "#334155";
                    current.style.borderColor = isActive ? "#0f172a" : "#dbe4f0";
                });

                cards.forEach((card) => {
                    const matches = selectedCategory === "all" || card.dataset.category === selectedCategory;
                    card.style.display = matches ? "flex" : "none";
                });
            });
        });

        if (window.lucide) window.lucide.createIcons();
    },
};
